import imageCompression from 'browser-image-compression';
import { convertToPixelCrop } from 'react-image-crop';

export const LOGO_ACCEPTED_EXTENSIONS = ['.png', '.webp', '.svg', '.avif'];
export const LOGO_ACCEPTED_MIME_TYPES = [
  'image/png',
  'image/webp',
  'image/svg+xml',
  'image/avif',
];
export const LOGO_ACCEPT_ATTRIBUTE =
  '.png,.webp,.svg,.avif,image/png,image/webp,image/svg+xml,image/avif';

export const LOGO_MAX_DISPLAY_DIMENSION = 2048;
export const LOGO_MAX_OUTPUT_DIMENSION = 4096;
export const LOGO_COMPRESSION_THRESHOLD_BYTES = 1024 * 1024;
export const LOGO_OUTPUT_FILE_NAME = 'logo.png';

const JPEG_EXTENSIONS = ['.jpg', '.jpeg'];
const JPEG_MIME_TYPES = ['image/jpeg', 'image/jpg'];

export function isJpegFile(file) {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  return (
    JPEG_EXTENSIONS.includes(extension) ||
    JPEG_MIME_TYPES.includes(file.type.toLowerCase())
  );
}

export function isAcceptedLogoFile(file) {
  if (isJpegFile(file)) return false;

  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (LOGO_ACCEPTED_EXTENSIONS.includes(extension)) return true;

  return LOGO_ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase());
}

export function getPixelCrop(image, crop) {
  const pixelCrop = convertToPixelCrop(crop, image.width, image.height);
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  return {
    x: pixelCrop.x * scaleX,
    y: pixelCrop.y * scaleY,
    width: pixelCrop.width * scaleX,
    height: pixelCrop.height * scaleY,
  };
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to export logo as PNG'));
        }
      },
      'image/png',
      1,
    );
  });
}

export function fitDisplayDimensionsWithinLimit(width, height, maxDimension) {
  const roundedWidth = Math.max(1, Math.round(width));
  const roundedHeight = Math.max(1, Math.round(height));

  if (roundedWidth <= maxDimension && roundedHeight <= maxDimension) {
    return {
      displayWidth: roundedWidth,
      displayHeight: roundedHeight,
    };
  }

  const scale = maxDimension / Math.max(roundedWidth, roundedHeight);

  return {
    displayWidth: Math.max(1, Math.round(roundedWidth * scale)),
    displayHeight: Math.max(1, Math.round(roundedHeight * scale)),
  };
}

export async function cropImageToRetinaPng(image, crop) {
  const pixelCrop = getPixelCrop(image, crop);
  const { displayWidth, displayHeight } = fitDisplayDimensionsWithinLimit(
    pixelCrop.width,
    pixelCrop.height,
    LOGO_MAX_DISPLAY_DIMENSION,
  );
  const outputWidth = displayWidth * 2;
  const outputHeight = displayHeight * 2;

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2d context is not available');
  }
  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  let blob = await canvasToPngBlob(canvas);

  if (blob.size > LOGO_COMPRESSION_THRESHOLD_BYTES) {
    const compressedFile = await imageCompression(
      new File([blob], LOGO_OUTPUT_FILE_NAME, { type: 'image/png' }),
      {
        maxSizeMB: 5,
        fileType: 'image/png',
        useWebWorker: true,
      },
    );
    blob = compressedFile;
  }

  const file = new File([blob], LOGO_OUTPUT_FILE_NAME, { type: 'image/png' });

  return {
    file,
    displayWidth,
    displayHeight,
  };
}

export function appendLogoToFormData(
  formData,
  fieldName,
  { file, displayWidth, displayHeight },
) {
  if (!file) return;

  formData.append(fieldName, file, LOGO_OUTPUT_FILE_NAME);

  if (Number.isFinite(displayWidth) && Number.isFinite(displayHeight)) {
    formData.append('displayWidth', String(displayWidth));
    formData.append('displayHeight', String(displayHeight));
  }
}
